import { useCallback, useEffect, useRef, useState } from 'react';

interface IProps {
  // 1 => the entire element must be visible, 0 => only a single pixel of the element needs to be visible
  threshold?: number;
  disabled?: boolean;

  // setIntersecting within x pixels of entering viewport
  rootMargin?: string;
  defaultIsVisible?: boolean;
}

// provides a ref callback that can be attached to any element to track its visibility in the viewport
const useOnScreen = (props?: IProps) => {
  const {
    threshold = 0.9,
    disabled = false,
    defaultIsVisible = false,
    rootMargin,
  } = props || {};

  const [isVisible, setIsVisible] = useState(defaultIsVisible);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRef = useRef<Element | null>(null);

  const refCallback = useCallback((node: Element | null) => {
    elementRef.current = node;
  }, []);

  useEffect(() => {
    if (disabled || !elementRef.current) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold, rootMargin }
    );

    observerRef.current.observe(elementRef.current);

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [threshold, rootMargin, disabled]);

  return { isVisible, ref: refCallback };
};

export default useOnScreen;
